import * as arrays from "common/arrays";
import * as ids from "common/ids";
import * as layerResolvers from "domain/layerResolvers";
import * as _ from "lodash";
import * as claimsActionTypes from "store/actions/input/claims/claimsActionTypes";
import * as programTowerActions from "store/actions/input/program/programTowerActions";
import * as persistenceActionTypes from "store/actions/persistence/persistenceActionTypes";
import * as actionTypes from "store/actions/pricing/pricingActionTypes";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as towerPricingActionTypes from "store/actions/pricing/tower/towerPricingActionTypes";
import * as towerPricingActions from "store/actions/pricing/tower/towerPricingActions";
import * as staticDataSelectors from "store/selectors/temp/staticData/staticDataSelectors";
import * as uuid from "uuid";

export const INIT_STATE = {
  activeLayer: 0,
  activePolicyId: null,
  policies: [],
  assumptions: {},
  baseLayer: {
    limit: 5e5,
    attachment: 5e5,
  },
  recommendedBaseLayer: null,
  layers: [],
  frequency: {
    note: null,
  },
  selectedExposureMetric: {
    key: "REVENUE",
    name: "Revenue",
    unit: "$",
  },
  insuredWeight: 20,
  improvexWeight: 80,
  claimsWeightsProbableError: false,
  towerPricingScalingLayer: 0,
  tower: {
    note: null,
  },
  note: null,
};

const newActiveLayer = (activeLayer, layersLength, removingLayer) => {
  if (activeLayer === layersLength - 1) {
    return layersLength - 2;
  }
  if (activeLayer > removingLayer) {
    return activeLayer - 1;
  }
  return activeLayer;
};

const updateActiveLayer = (state, key, value) => {
  return updateActiveLayerWithDict(state, { [key]: value });
};

const updateActiveLayerWithDict = (state, values) => {
  return updateActiveLayerWithLambda(state, (layer) => ({
    ...layer,
    ...values,
  }));
};

const updateActiveLayerWithLambda = (state, modifyActiveLayer) => {
  return (state?.layers ?? []).map((layer, index) =>
    index === state?.activeLayer ? modifyActiveLayer(layer) : layer
  );
};

const updateLayer = ({ state, layerId, values }) => ({
  ...state,
  layers: (state.layers ?? []).map((layer) =>
    layer.id === layerId
      ? {
          ...layer,
          ...values,
        }
      : layer
  ),
});

const createNewTowerLayer = () => ({
  id: ids.makeNewId(),
});

const createNewTowerShare = () => ({
  id: ids.makeNewId(),
});

const updateTowerShare = ({ state, layerIndex, shareIndex, values }) => {
  const layers = state.tower?.layers ?? [];
  const layer = layers[layerIndex] ?? createNewTowerLayer();
  const shares = layer.shares ?? [];
  const share = shares?.[shareIndex] ?? createNewTowerShare();

  const newShare = {
    ...share,
    ...values,
  };

  [
    ["share", "shareOfLimit"],
    ["shareOfLimit", "share"],
  ].forEach(([key, keyToClear]) => {
    if (key in values) {
      delete newShare[keyToClear];
      newShare[key] = values[key];
    }
  });

  return {
    ...state,
    tower: {
      ...state.tower,
      selectedLayer: Math.max(state.tower?.selectedLayer ?? 0, 0),
      layers: [
        ...layers.slice(0, layerIndex),
        {
          ...layer,
          shares: [
            ...shares.slice(0, shareIndex),
            newShare,
            ...shares.slice(shareIndex + 1),
          ],
        },
        ...layers.slice(layerIndex + 1),
      ],
    },
  };
};

const getKey = (action) => {
  switch (action.type) {
    case towerPricingActionTypes.UPDATE_TOWER_CARRIER:
      return "carrier";
    case towerPricingActionTypes.UPDATE_TOWER_PRIMARIES_LIMIT:
    case towerPricingActionTypes.UPDATE_TOWER_LIMIT:
      return "limit";
    case towerPricingActionTypes.UPDATE_TOWER_PRIMARIES_ATTACHMENT:
    case towerPricingActionTypes.UPDATE_TOWER_ATTACHMENT:
      return "attachment";
    case towerPricingActionTypes.UPDATE_TOWER_SELECTED:
      return "selectedLayer";
    case towerPricingActionTypes.UPDATE_TOWER_MARKET_GROSS_WRITTEN_PREMIUM:
      return "grossPremium";
    case towerPricingActionTypes.UPDATE_TOWER_PRICING_NOTE:
      return "note";
    case towerPricingActionTypes.UPDATE_TOWER_PRIMARIES_COMMENT:
      return "comment";
    default:
      return undefined;
  }
};

const applyProgramTowerToState = (state, layers) => {
  return {
    ...state,
    layers,
    tower: {
      ...state.tower,
      layers: layers.map((layer) => {
        return {
          id: uuid.v5(layer.id, uuid.NIL),
          limit: layer.limit,
          attachment: layer.attachment,
          grossPremium: layer.grossPremium,
        };
      }),
    },
  };
};

const updateProgramTowerLayer = (state, action) => {
  const { layerIndex, key, value } = action.payload;
  let oldLayers = state.layers ?? [];
  if (layerIndex >= oldLayers.length) {
    oldLayers = [...oldLayers, createNewLayer(null)];
  }
  const layers = [
    ...oldLayers.slice(0, layerIndex),
    {
      ...oldLayers[layerIndex],
      [key]: value,
    },
    ...oldLayers.slice(layerIndex + 1),
  ];
  return applyProgramTowerToState(state, layers);
};

const deleteProgramTowerLayer = (state, action) => {
  const { layerIndex } = action.payload;
  const oldLayers = state.layers ?? [];
  const layers = [
    ...oldLayers.slice(0, layerIndex),
    ...oldLayers.slice(layerIndex + 1),
  ];
  return applyProgramTowerToState(state, layers);
};

const createNewLayer = (state, overrides = {}) => {
  const reinsurersConfig =
    staticDataSelectors.selectConfig("reinsurers")(state) ?? {};
  const defaultPaperProvider =
    reinsurersConfig.defaultPaperProvider ??
    reinsurersConfig?.default?.[0] ??
    null;
  const defaultPaperProviderFields = !!defaultPaperProvider
    ? { paperProvider: defaultPaperProvider }
    : {};

  return {
    id: ids.makeNewId(),
    status: "DRAFT",
    pricing: {
      selectedModel:
        staticDataSelectors.selectAnalyticsConfig(state)?.layerPricing
          ?.defaultModel ??
        "UNEXPECTEDLY_THE_SELECTED_PRICING_MODEL_HAS_NOT_BEEN_SET",
    },
    ...defaultPaperProviderFields,
    ...overrides,
  };
};

const updatePricingInput = ({ state, key, layerId = null, values }) => {
  const pricingInput = state.pricingInputs?.[key];

  const newPricingInput = !!layerId
    ? {
        ...pricingInput,
        layers: {
          ...pricingInput?.layers,
          [layerId]: {
            ...pricingInput?.layers?.[layerId],
            ...values,
          },
        },
      }
    : {
        ...pricingInput,
        ...values,
      };

  return {
    ...state,
    pricingInputs: {
      ...state.pricingInputs,
      [key]: newPricingInput,
    },
  };
};

const convertExpiringLayerToTowerLayer = (layer) => {
  const carrierText = layer.carrier || "";
  const shares = [];

  if (carrierText.includes("/")) {
    carrierText.split("/").forEach((carrier) => {
      shares.push({
        ...createNewTowerShare(),
        carrier: carrier.trim() || "",
        share: null,
      });
    });
  } else {
    shares.push({
      ...createNewTowerShare(),
      carrier: carrierText.trim(),
      share: 100,
    });
  }

  return {
    ...createNewTowerLayer(),
    limit: layer.limit,
    attachment: layer.attachment,
    grossPremium: layer.grossPremium,
    shares: shares,
  };
};

const pricingReducer = (state, action) => {
  if (state == null) {
    state = INIT_STATE;
  }

  const key = getKey(action);

  switch (action.type) {
    case persistenceActionTypes.LOAD_SUBMISSION_STARTED:
      return {
        ...INIT_STATE,
      };
    case persistenceActionTypes.LOAD_SUBMISSION_SUCCEEDED:
      return {
        ...INIT_STATE,
        ...Object.fromEntries(
          Object.entries(action.payload?.data?.state?.pricing ?? {})
            .filter(([k, v]) => !(k === "baseLayer" && v == null))
            .map(([k, v]) => [
              k,
              k === "layers"
                ? (v ?? []).map((layer) => ({
                    ...layer,
                    loadingPurePremium: false,
                  }))
                : v,
            ])
        ),
      };
    case actionTypes.UPDATE_LAYER:
      return updateLayer({
        ...action.payload,
        state,
      });
    case actionTypes.UPDATE_CLAIMS_DATE_RANGE:
      return {
        ...state,
        claimsDateRange: action.payload,
      };
    case actionTypes.UPDATE_CLAIMS_INCLUDED_LOSS_TYPES:
      return {
        ...state,
        claimsIncludedLossTypes: action.payload,
      };
    case actionTypes.UPDATE_LAYER_COMMENT:
      return {
        ...state,
        layers: updateActiveLayer(state, "comment", action.payload),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_LIMIT:
      return {
        ...state,
        layers: updateActiveLayer(state, "limit", action.payload),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_ATTACHMENT:
      return {
        ...state,
        layers: updateActiveLayer(state, "attachment", action.payload),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_LINE_SIZE_ABSOLUTE:
      return {
        ...state,
        layers: updateActiveLayerWithDict(state, {
          lineSize: action.payload,
          shareOfLine: null,
        }),
      };
    case actionTypes.UPDATE_GROSS_PREMIUM:
      return {
        ...state,
        layers: updateActiveLayerWithDict(state, {
          grossPremium: action.payload,
          grossPPM: null,
        }),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_GROSS_PPM:
      return {
        ...state,
        layers: updateActiveLayerWithDict(state, {
          grossPremium: null,
          grossPPM: action.payload,
        }),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_SHARE_OF_LINE:
      return {
        ...state,
        layers: updateActiveLayerWithDict(state, {
          shareOfLine: action.payload,
          lineSize: null,
        }),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_TRIA:
      return {
        ...state,
        layers: updateActiveLayerWithDict(state, {
          TRIA: action.payload,
        }),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_SHARE_OF_PREMIUM:
      return {
        ...state,
        layers: updateActiveLayerWithDict(state, {
          shareOfPremium: action.payload,
        }),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_AUTHORIZED_LIMIT:
      return {
        ...state,
        layers: updateActiveLayer(state, "authorizedLimit", action.payload),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_BROKERAGE:
      return {
        ...state,
        layers: updateActiveLayer(state, "brokerage", action.payload),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_OTHER_ACQUISITION_COSTS:
      return {
        ...state,
        layers: updateActiveLayer(
          state,
          "otherAcquisitionCosts",
          action.payload
        ),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_PROFIT:
      return {
        ...state,
        layers: updateActiveLayer(state, "profit", action.payload),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_EXPENSES:
      return {
        ...state,
        layers: updateActiveLayer(state, "expenses", action.payload),
      };
    case pricingActions.setActiveLayerPricingSelectedModel.toString():
      return {
        ...state,
        layers: (state.layers ?? []).map((layer, index) => {
          return index !== state.activeLayer
            ? layer
            : {
                ...layer,
                pricing: {
                  ...layer.pricing,
                  selectedModel: action.payload.model,
                },
              };
        }),
      };
    case pricingActions.setActiveLayerPricingSelectedPrice.toString():
      return {
        ...state,
        layers: (state.layers ?? []).map((layer, index) => {
          return index !== state.activeLayer
            ? layer
            : {
                ...layer,
                pricing: {
                  ...layer.pricing,
                  selectedPrices: {
                    ...layer.pricing.selectedPrices,
                    [action.payload.model]: action.payload.value,
                  },
                },
              };
        }),
      };
    case actionTypes.SET_ACTIVE_LAYER_POLICY_FORM:
      return {
        ...state,
        layers: updateActiveLayer(state, "policyForm", action.payload),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_REFERENCE:
      return {
        ...state,
        layers: updateActiveLayer(state, "reference", action.payload),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_REINSURANCE:
      return {
        ...state,
        layers: updateActiveLayer(state, "paperProvider", action.payload),
      };
    case actionTypes.UPDATE_MINIMUM_EARNED_PREMIUM:
      return {
        ...state,
        layers: updateActiveLayer(
          state,
          "minimumEarnedPremium",
          action.payload
        ),
      };
    case actionTypes.UPDATE_ALL_LAYERS_REINSURANCE:
      return {
        ...state,
        layers: (state.layers ?? []).map((layer) => ({
          ...layer,
          paperProvider: action.payload,
        })),
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_PAYMENT_TERMS:
      return {
        ...state,
        layers: updateActiveLayer(
          state,
          "paymentTerms",
          action.payload ?? null
        ),
      };
    case actionTypes.UPDATE_INSURED_WEIGHT:
      const insuredWeight = parseFloat(action.payload);
      return {
        ...state,
        insuredWeight,
        improvexWeight: 100 - insuredWeight,
        claimsWeightsProbableError: 1 >= insuredWeight && insuredWeight > 0,
      };
    case actionTypes.UPDATE_IMPROVEX_WEIGHT:
      const improvexWeight = parseFloat(action.payload);
      return {
        ...state,
        insuredWeight: 100 - improvexWeight,
        improvexWeight,
        claimsWeightsProbableError: 1 >= improvexWeight && improvexWeight > 0,
      };
    case claimsActionTypes.USE_FILTER_AS_BASE_LAYER:
      return {
        ...state,
        baseLayer: {
          ...state.baseLayer,
          ...action.payload,
        },
      };
    case actionTypes.UPDATE_BASE_LAYER_LIMIT:
      return {
        ...state,
        baseLayer: {
          ...state.baseLayer,
          limit: action.payload,
        },
      };
    case actionTypes.UPDATE_BASE_LAYER_FROM:
      return {
        ...state,
        baseLayer: {
          ...state.baseLayer,
          from: action.payload,
        },
      };
    case actionTypes.UPDATE_BASE_LAYER_TO:
      return {
        ...state,
        baseLayer: {
          ...state.baseLayer,
          to: action.payload,
        },
      };
    case actionTypes.UPDATE_BASE_LAYER_ATTACHMENT:
      return {
        ...state,
        baseLayer: {
          ...state.baseLayer,
          attachment: action.payload,
        },
      };
    case actionTypes.UPDATE_BASE_LAYER_EXPECTED_NUMBER_OF_BLENDED_LOSSES:
      return {
        ...state,
        baseLayer: {
          ...state.baseLayer,
          expectedLosses: action.payload,
        },
      };
    case actionTypes.UPDATE_NUMBER_OF_EVENTS_AT_INSURED_IMPACTING_BASE_LAYER:
      return {
        ...state,
        frequency: {
          ...state.frequency,
          underwriterSelectedNumberOfClaims: action.payload,
        },
      };
    case actionTypes.UPDATE_CHOSEN_EXPOSURE_METRIC:
      return {
        ...state,
        selectedExposureMetric: action.payload,
      };
    case actionTypes.REFRESH_ACTIVE_LAYER_PRICING:
      return {
        ...state,
        layers: updateActiveLayer(state, "loadingPurePremium", true),
      };
    case actionTypes.ACTIVE_LAYER_PRICING_RETURNED:
      let layers = [...(state.layers ?? [])];
      let oldLayer = layers[action.payload.query.targetLayer];
      if (oldLayer.loadingPurePremium) {
        layers[action.payload.query.targetLayer] = {
          ...oldLayer,
          pricing: {
            ...oldLayer.pricing,
            prices: action.payload.prices,
            selectedMeasures: action.payload.selectedMeasures,
            defaultModel: action.payload.defaultModel,
          },
          loadingPurePremium: false,
          hasError: false,
        };
        return {
          ...state,
          layers: layers,
        };
      }
      return state;
    case actionTypes.ACTIVE_LAYER_PRICING_FAILED:
      return {
        ...state,
        layers: [
          ...(state.layers ?? []).slice(0, action.payload.targetLayer),
          {
            ...(state.layers ?? [])[action.payload.targetLayer],
            hasError: true,
            loadingPurePremium: false,
          },
          ...(state.layers ?? []).slice(action.payload.targetLayer + 1),
        ],
      };
    case actionTypes.UPDATE_RECOMMENDED_BASE_LAYER:
      return {
        ...state,
        recommendedBaseLayer: action.payload ?? null,
      };
    case actionTypes.RECALCULATING_INSURED_EXPECTED_EVENTS:
      return {
        ...state,
        frequency: {
          ...state.frequency,
          loadingInsuredExpectedEvents: true,
        },
      };
    case actionTypes.RECALCULATED_INSURED_EXPECTED_EVENTS:
      return {
        ...state,
        frequency: {
          ...state.frequency,
          ...action.payload.data,
          request: action.payload.request,
          loadingInsuredExpectedEvents: false,
          loadingInsuredExpectedEventsFailed: false,
        },
      };
    case actionTypes.RECALCULATED_INSURED_EXPECTED_EVENTS_FAILED:
      return {
        ...state,
        frequency: {
          ...state.frequency,
          loadingInsuredExpectedEvents: false,
          loadingInsuredExpectedEventsFailed: true,
        },
      };
    case towerPricingActionTypes.UPDATE_TOWER_LIMIT:
    case towerPricingActionTypes.UPDATE_TOWER_CARRIER:
    case towerPricingActionTypes.UPDATE_TOWER_ATTACHMENT:
    case towerPricingActionTypes.UPDATE_TOWER_MARKET_GROSS_WRITTEN_PREMIUM:
      return {
        ...state,
        tower: {
          ...state.tower,
          selectedLayer: Math.max(state.tower?.selectedLayer ?? 0, 0),
          layers: [
            ...(state.tower?.layers ?? []).slice(0, action.payload.index),
            {
              ...createNewTowerLayer(),
              ...(state.tower?.layers ?? [])[action.payload.index],
              [key]: action.payload[key],
            },
            ...(state.tower?.layers ?? []).slice(action.payload.index + 1),
          ],
        },
      };
    case towerPricingActionTypes.UPDATE_TOWER_PRIMARIES_LIMIT:
    case towerPricingActionTypes.UPDATE_TOWER_PRIMARIES_ATTACHMENT:
    case towerPricingActionTypes.UPDATE_TOWER_PRIMARIES_COMMENT:
      return {
        ...state,
        primaryLayers: {
          ...state.primaryLayers,
          layers: [
            ...(state.primaryLayers?.layers ?? []).slice(
              0,
              action.payload.index
            ),
            {
              ...(state.primaryLayers?.layers ?? [])[action.payload.index],
              [key]: action.payload[key],
            },
            ...(state.primaryLayers?.layers ?? []).slice(
              action.payload.index + 1
            ),
          ],
        },
      };
    case towerPricingActionTypes.ADD_TOWER_PRIMARIES_LAYER:
      return {
        ...state,
        primaryLayers: {
          ...state.primaryLayers,
          layers: [
            ...(state.primaryLayers?.layers ?? []).slice(0, action.payload),
            {},
            ...(state.primaryLayers?.layers ?? []).slice(action.payload),
          ],
        },
      };
    case towerPricingActionTypes.DELETE_TOWER_PRIMARIES_LAYER:
      return {
        ...state,
        primaryLayers: {
          ...state.primaryLayers,
          layers: [
            ...(state.primaryLayers?.layers ?? []).slice(0, action.payload),
            ...(state.primaryLayers?.layers ?? []).slice(action.payload + 1),
          ],
        },
      };
    case towerPricingActionTypes.UPDATE_SCHEDULE_OF_UNDERLYING_FIELD:
      return {
        ...state,
        scheduleOfUnderlying: {
          ...state.scheduleOfUnderlying,
          layers: (state.scheduleOfUnderlying?.layers ?? []).map((layer) =>
            layer.id === action.payload.id
              ? { ...layer, [action.payload.key]: action.payload.value }
              : layer
          ),
        },
      };
    case towerPricingActionTypes.ADD_SCHEDULE_OF_UNDERLYING_ROW:
      return {
        ...state,
        scheduleOfUnderlying: {
          ...state.scheduleOfUnderlying,
          layers: [
            ...(state.scheduleOfUnderlying?.layers ?? []),
            { id: ids.makeNewId(), print: true },
          ],
        },
      };
    case towerPricingActionTypes.DELETE_SCHEDULE_OF_UNDERLYING_ROW:
      return {
        ...state,
        scheduleOfUnderlying: {
          ...state.scheduleOfUnderlying,
          layers: (state.scheduleOfUnderlying?.layers ?? []).filter(
            (layer) => layer.id !== action.payload
          ),
        },
      };
    case towerPricingActionTypes.MOVE_SCHEDULE_OF_UNDERLYING_ROW:
      // The stored array order is the display and print order, so moving a
      // row here is enough for the Summary view and the generated schedule.
      return {
        ...state,
        scheduleOfUnderlying: {
          ...state.scheduleOfUnderlying,
          layers: arrays.move(
            state.scheduleOfUnderlying?.layers ?? [],
            action.payload
          ),
        },
      };
    case towerPricingActionTypes.UPDATE_TOWER_SHARE:
      return updateTowerShare({
        state,
        ...action.payload,
      });
    case towerPricingActionTypes.DELETE_TOWER_SHARE:
      return {
        ...state,
        tower: {
          ...state.tower,
          layers: [
            ...(state.tower?.layers ?? []).slice(0, action.payload.layerIndex),
            {
              ...(state.tower?.layers ?? [])[action.payload.layerIndex],
              shares: [
                ...(
                  (state.tower?.layers ?? [])[action.payload.layerIndex]
                    ?.shares ?? []
                ).slice(0, action.payload.shareIndex),
                ...(
                  (state.tower?.layers ?? [])[action.payload.layerIndex]
                    ?.shares ?? []
                ).slice(action.payload.shareIndex + 1),
              ],
            },
            ...(state.tower?.layers ?? []).slice(action.payload.layerIndex + 1),
          ],
        },
      };
    case towerPricingActionTypes.UPDATE_TOWER_PRICING_NOTE:
    case towerPricingActionTypes.UPDATE_TOWER_SELECTED:
      return {
        ...state,
        tower: {
          ...state.tower,
          [key]: action.payload,
        },
      };
    case towerPricingActionTypes.ADD_TOWER_LAYER:
      return {
        ...state,
        tower: {
          ...state.tower,
          selectedLayer:
            (state.tower?.selectedLayer ?? 0) >= action.payload
              ? (state.tower?.selectedLayer ?? 0) + 1
              : state.tower?.selectedLayer ?? 0,
          layers: [
            ...(state.tower?.layers ?? []).slice(0, action.payload),
            createNewTowerLayer(),
            ...(state.tower?.layers ?? []).slice(action.payload),
          ],
        },
      };
    case towerPricingActionTypes.DELETE_TOWER_LAYER:
      return {
        ...state,
        tower: {
          ...state.tower,
          selectedLayer: newActiveLayer(
            state.tower?.selectedLayer,
            (state.tower?.layers ?? []).length,
            action.payload
          ),
          layers: [
            ...(state.tower?.layers ?? []).slice(0, action.payload),
            ...(state.tower?.layers ?? []).slice(action.payload + 1),
          ],
        },
      };
    case towerPricingActionTypes.UPDATE_TOWER_LAYER:
      return {
        ...state,
        tower: {
          ...state.tower,
          layers: [
            ...(state.tower?.layers ?? []).slice(0, action.payload.index),
            {
              ...createNewTowerLayer(),
              ...state.tower?.layers?.[action.payload.index],
              ...action.payload.values,
            },
            ...(state.tower?.layers ?? []).slice(action.payload.index + 1),
          ],
        },
      };
    case towerPricingActionTypes.UPDATE_ALL_TOWER_LAYERS_INCLUDE_SIR: {
      const { sirId, applyAll } = action.payload;
      return {
        ...state,
        tower: {
          ...state.tower,
          layers: (state.tower?.layers ?? []).map((layer) =>
            applyAll || layer.includeSir == null
              ? { ...layer, includeSir: sirId }
              : layer
          ),
        },
      };
    }
    case towerPricingActionTypes.COPY_FROM_EXPIRING:
      return {
        ...state,
        tower: {
          ...state.tower,
          layers: (action?.payload ?? []).map(convertExpiringLayerToTowerLayer),
        },
      };
    case towerPricingActions.calculateTowerPricing.started.toString():
      return {
        ...state,
        tower: {
          ...state.tower,
          updatingTowerPricing: true,
        },
      };
    case towerPricingActions.calculateTowerPricing.succeeded.toString():
      return {
        ...state,
        tower: {
          ...state.tower,
          updatingTowerPricing: false,
          error: null,
          layers: (state.tower?.layers ?? []).map((responseLayer, index) => ({
            ...responseLayer,
            towerPricing: action.payload.layers?.[index] ?? {},
          })),
        },
      };
    case towerPricingActions.calculateTowerPricing.failed.toString():
      return {
        ...state,
        tower: {
          ...state.tower,
          updatingTowerPricing: false,
          error: action.payload ?? "Pricing failed",
        },
      };
    case actionTypes.ADD_LAYER:
      return {
        ...state,
        activeLayer: state.layers?.length ?? 0,
        layers: [
          ...(state.layers ?? []),
          createNewLayer(action.payload.state, action.payload.initialData),
        ],
      };
    case actionTypes.DELETE_LAYER: {
      const deletedLayerId = state.layers?.[action.payload]?.id;
      return {
        ...state,
        activeLayer: newActiveLayer(
          state.activeLayer,
          state.layers?.length ?? 0,
          action.payload
        ),
        towerPricingScalingLayer: newActiveLayer(
          state.towerPricingScalingLayer,
          state.layers?.length ?? 0,
          action.payload
        ),
        layers: [
          ...(state.layers ?? []).slice(0, action.payload),
          ...(state.layers ?? []).slice(action.payload + 1),
        ],
        policies: deletedLayerId
          ? (state.policies ?? []).map((policy) => ({
              ...policy,
              layers: (policy.layers ?? []).filter(
                (id) => id !== deletedLayerId
              ),
            }))
          : state.policies,
      };
    }
    case actionTypes.UPDATE_ACTIVE_LAYER:
      return {
        ...state,
        activeLayer: action.payload,
        activePolicyId: null,
      };
    case actionTypes.UPDATE_ACTIVE_LAYER_STATUS: {
      const activePolicy = (state.policies ?? []).find(
        (p) => p.id === state.activePolicyId
      );
      if (activePolicy) {
        const policyLayerIds = new Set(activePolicy.layers ?? []);
        return {
          ...state,
          layers: (state.layers ?? []).map((layer) =>
            policyLayerIds.has(layer.id)
              ? { ...layer, status: action.payload, statusReason: null }
              : layer
          ),
        };
      }
      return {
        ...state,
        layers: updateActiveLayerWithDict(state, {
          status: action.payload,
          statusReason: null,
        }),
      };
    }
    case actionTypes.CREATE_POLICY:
      return {
        ...state,
        policies: [
          ...(state.policies ?? []),
          { ...action.payload, id: ids.makeNewId() },
        ],
      };
    case actionTypes.DELETE_POLICY:
      return {
        ...state,
        policies: (state.policies ?? []).filter(
          (p) => p.id !== action.payload.id
        ),
        activePolicyId:
          state.activePolicyId === action.payload.id
            ? null
            : state.activePolicyId,
      };
    case actionTypes.UPDATE_POLICY:
      return {
        ...state,
        policies: (state.policies ?? []).map((p) =>
          p.id === action.payload.id
            ? {
                ...p,
                ...(action.payload.name !== undefined
                  ? { name: action.payload.name }
                  : {}),
                ...(action.payload.layers !== undefined
                  ? { layers: action.payload.layers }
                  : {}),
              }
            : p
        ),
      };
    case actionTypes.SET_ACTIVE_POLICY:
      return {
        ...state,
        activePolicyId: action.payload.id,
      };
    case actionTypes.SET_AS_BASE_LAYER_FOR_TOWER_PRICING_SCALING:
      return {
        ...state,
        towerPricingScalingLayer: action.payload,
      };
    case actionTypes.UPDATE_LAYER_STATUS:
      return {
        ...state,
        layers: [
          ...(state.layers ?? []).slice(0, action.payload.index),
          {
            ...(state.layers ?? [])[action.payload.index],
            status: action.payload.status,
            statusReason: null,
          },
          ...(state.layers ?? []).slice(action.payload.index + 1),
        ],
      };
    case actionTypes.UPDATE_LAYER_PRICING_NOTE:
      return {
        ...state,
        note: action.payload,
      };
    case actionTypes.UPDATE_FREQUENCY_PRICING_NOTE:
      return {
        ...state,
        frequency: {
          ...state.frequency,
          note: action.payload,
        },
      };
    case programTowerActions.updateValue.toString():
      return updateProgramTowerLayer(state, action);
    case programTowerActions.deleteLayer.toString():
      return deleteProgramTowerLayer(state, action);
    case pricingActions.addPremiumAdjustment.toString():
      return {
        ...state,
        layers: (state.layers ?? []).map((layer) => {
          if (layer.id === action.payload.layerId) {
            return {
              ...layer,
              premiumAdjustments: [
                ...(layer.premiumAdjustments ?? []),
                action.payload.premiumAdjustment,
              ],
            };
          } else {
            return layer;
          }
        }),
      };
    case pricingActions.createPremiumAdjustment.toString():
      return {
        ...state,
        layers: (state.layers ?? []).map((layer) => {
          if (layer.id === action.payload.layerId) {
            return {
              ...layer,
              premiumAdjustments: [
                ...(layer.premiumAdjustments ?? []),
                action.payload.premiumAdjustment,
              ],
            };
          } else {
            return layer;
          }
        }),
      };
    case pricingActions.editPremiumAdjustment.toString():
      return {
        ...state,
        layers: (state.layers ?? []).map((layer) => {
          if (layer.id === action.payload.layerId) {
            return {
              ...layer,
              premiumAdjustments: (layer.premiumAdjustments ?? []).map(
                (premiumAdjustment) => {
                  if (
                    premiumAdjustment.id === action.payload.premiumAdjustmentId
                  ) {
                    return {
                      ...premiumAdjustment,
                      ...action.payload.edits,
                    };
                  } else {
                    return premiumAdjustment;
                  }
                }
              ),
            };
          } else {
            return layer;
          }
        }),
      };
    case pricingActions.deletePremiumAdjustment.toString():
      return {
        ...state,
        layers: (state.layers ?? []).map((layer) => {
          if (layer.id === action.payload.layerId) {
            return {
              ...layer,
              premiumAdjustments: (layer.premiumAdjustments ?? []).filter(
                (premiumAdjustment) =>
                  premiumAdjustment.id !== action.payload.premiumAdjustmentId
              ),
            };
          } else {
            return layer;
          }
        }),
      };
    case pricingActions.deleteActiveLayerCustomOverrides.toString():
      return {
        ...state,
        layers: updateActiveLayerWithLambda(state, (layer) => {
          const { customOverrides, ...restOfLayer } = layer;
          return restOfLayer;
        }),
      };
    case pricingActions.deletePremiumAdjustmentCustomOverrides.toString():
      return {
        ...state,
        layers: (state.layers ?? []).map((layer) => {
          if (layer.id === action.payload.layerId) {
            return {
              ...layer,
              premiumAdjustments: (layer.premiumAdjustments ?? []).map(
                (premiumAdjustment) => {
                  if (
                    premiumAdjustment.id === action.payload.premiumAdjustmentId
                  ) {
                    const { customOverrides, ...rest } =
                      premiumAdjustment ?? {};
                    return rest;
                  } else {
                    return premiumAdjustment;
                  }
                }
              ),
            };
          } else {
            return layer;
          }
        }),
      };
    case actionTypes.UPDATE_PRICING_INPUT:
      return updatePricingInput({
        ...action.payload,
        state,
      });

    case actionTypes.SET_PRICING_ASSUMPTION:
      return {
        ...state,
        assumptions: {
          ...(state.assumptions || {}),
          ...action.payload,
        },
      };

    default:
      return state;
  }
};

const withPricingLayerSync = (reducer) => (state, action) => {
  const result = reducer(state, action);
  if (state?.syncPricingLayersToTowerLayers) {
    const layers = layerResolvers.extractLayersFromTowerLayers(
      result.tower?.layers ?? []
    );
    return _.isEqual(result.layers, layers)
      ? result
      : {
          ...result,
          layers,
        };
  } else {
    return result;
  }
};

export default withPricingLayerSync(pricingReducer);
